<template>
  <div v-if="question">
    <b-card class="mb-3 site text-container">
      <div class="row">
        <div class="col">
          <h2>{{ question.stage.title }}</h2>
        </div>
      </div>

      <hr>

      <div v-show="showSpinner" class="text-center">
        <b-spinner
          style="width: 3rem; height: 3rem;"
          label="Loading..."
        />
      </div>

      <div v-show="!showSpinner" class="row">
        <div class="col-sm">
          <p class="question-text">
            {{ question.title }}
          </p>
          <p v-show="question.tooltip && !showExplanation" class="explanation-toggle cursor"
             @click="showExplanation = true">
            Show
            Example
          </p>
          <p v-show="question.tooltip && showExplanation" class="explanation-toggle cursor"
             @click="showExplanation = false">
            Hide
            Example
          </p>
          <transition name="explanation">
            <p v-show="question.tooltip && showExplanation" class="explanation" v-html="question.tooltip" />
          </transition>
        </div>
      </div>

      <div v-show="!showSpinner"
           class="row mt-4 text-center options d-flex justify-content-center flex-wrap flex-md-nowrap">
        <button ref="value-2" class="btn btn-primary"
                :class="{ selected: question.val === 2 }"
                @focus="selectVal(2)"
                @click="selectVal(2)">
          Yes
        </button>
        <button ref="value-1" class="btn btn-primary"
                :class="{ selected: question.val === 1 }"
                @focus="selectVal(1)"
                @click="selectVal(1)">
          Room to Improve
        </button>
        <button ref="value-0" class="btn btn-primary"
                :class="{ selected: question.val === 0 }"
                @focus="selectVal(0)"
                @click="selectVal(0)">
          No
        </button>
        <button ref="value-null" class="btn btn-primary"
                :class="{ selected: question.val === null }"
                @focus="selectVal(null)"
                @click="selectVal(null)">
          Not Applicable
        </button>
      </div>

      <div class="row mt-4">
        <div class="col">
          <a class="cursor float-right right-note"
             :disabled="parentLoading"
             @click="clickScreenShot">
            Add Screenshot
          </a>
          <div v-if="imageNotes.length" class="mb-3">
            <p class="strong">
              Screenshots
            </p>
            <p v-for="item in imageNotes" :key="item.id">
              <a class="image-note-link"
                 target="_blank"
                 :href="item.url">
                <span class="mr-2" v-html="fileMediaSVG" />{{ renderDateAndTime(item.created_at) }}
              </a>
              <span class="cursor float-right"
                    @click="showDeleteImageNote(item)"
                    v-html="trashcanSVG" />
            </p>
          </div>
          <a v-show="!showToggle && !hasNote"
             class="cursor float-right right-note"
             :disabled="parentLoading"
             @click="showNote">
            Add Note
          </a>
          <b-form-textarea
            v-show="hasNote || showToggle"
            ref="textarea"
            v-model="question.note"
            placeholder="Add a note..."
            rows="3"
            max-rows="6"
            @focus="notesFocus(true)"
            @input="notesFocus(true)"
            @blur="notesFocus(false)"
          />
        </div>
      </div>

      <b-modal id="delete-image-note-modal" class="mt-5" hide-footer hide-header>
        <div class="d-block text-center">
          <h3>Are you sure?</h3>
          <LoadingWrapper :loading-bool="loadingImg" spinner-class="mb-5">
            <p>
              Once you delete this image there's no way to re-download it from Heurix in future.
            </p>
          </LoadingWrapper>
          <b-alert v-for="error in imgErrors" :key="error" show variant="danger">
            {{ error }}
          </b-alert>
          <button class="btn btn-primary mb-2"
                  :disabled="loadingImg"
                  @click="deleteImageNoteWrapper(deletingImageNote)">
            Delete
          </button>
          <button class="btn btn-secondary" @click="$bvModal.hide('delete-image-note-modal')">
            Back
          </button>
        </div>
      </b-modal>

      <b-modal id="image-note-modal" class="mt-5" hide-footer hide-header>
        <div class="d-block text-center">
          <h3>Upload a Screenshot</h3>
          <LoadingWrapper :loading-bool="loadingImg" spinner-class="mb-5">
            <p v-if="!isVerified">
              You have to verify your email address before you can upload screenshots. You can do
              that
              <a :href="resolve('user.details')" @click="navigate('user.details', $event)">here.</a>
            </p>
            <image-uploader v-if="isVerified"
                            :max-width="1920"
                            :max-height="1920"
                            :quality="0.95"
                            :auto-rotate="true"
                            :preview="true"
                            accept="image/*"
                            output-format="verbose"
                            do-not-resize="['gif', 'svg']"
                            @input="onInput"
            />
          </LoadingWrapper>
          <b-alert v-for="error in imgErrors" :key="error" show variant="danger">
            {{ error }}
          </b-alert>
          <button class="btn btn-primary mb-2" :disabled="loadingImg || !tmpImg" @click="saveImageNoteWrapper">
            Save
          </button>
          <button class="btn btn-secondary" @click="closeImageNoteWrapper">
            Back
          </button>
        </div>
      </b-modal>

      <Errors :errors="loadingErrors" />

      <div class="row navigation">
        <div class="col">
          <button v-show="questionIndex > 1"
                  ref="back"
                  class="btn btn-secondary backward"
                  :disabled="parentLoading"
                  @focus="backHasFocus = true"
                  @blur="blurBack"
                  @click="back">
            <span v-html="arrowLeftSVG" /> Back
          </button>
          <div class="forward">
            <button class="btn btn-primary float-right"
                    :disabled="!canSubmit"
                    @click="done()">
              Next
              <span v-html="arrowRightSVG" />
            </button>
            <span class="button-hint"
                  :class="{disabled: !canSubmit || notesHasFocus}">press <strong>Enter</strong> ↵</span>
          </div>
        </div>
      </div>
      <p class="selector text-center mt-4">
        Question {{ questionIndex }} / {{ questionCount }}
      </p>
    </b-card>
  </div>
</template>

<script>
  import octicons from "@primer/octicons"
  import LoadingWrapper from "../../navigation/LoadingWrapper"
  import keybindMixin from "../../../utils/keybind"
  import datesMixin from "../../../utils/dates"
  import ImageUploader from 'vue-image-upload-resize'
  import b64toBlob from 'b64-to-blob'
  import apiMixin from "../../../utils/api"
  import Errors from "../../errors"

  export default {
    name: 'EvaluationSubcomponentsQuestion',
    components: {Errors, ImageUploader, LoadingWrapper},
    mixins: [apiMixin, datesMixin, keybindMixin],
    props: {
      question: {type: Object, required: true},
      next: {type: Function, required: true},
      prev: {type: Function, required: true},
      parentLoading: {type: Boolean, required: true},
      loadingErrors: {type: Array, required: true},
      questionCount: {type: Number, required: true},
      questionIndex: {type: Number, required: true},
      imageNotes: {type: Array, required: false, default: () => []},
      deleteImageNote: {type: Function, required: true},
      saveImageNote: {type: Function, required: true},
    },
    data() {
      return {
        showToggle: false,
        arrowLeftSVG: octicons["arrow-left"].toSVG({height: '16px', fill: '#fff'}),
        arrowRightSVG: octicons["arrow-right"].toSVG({height: '16px', fill: '#fff'}),

        timeoutId: null,
        showSpinner: false,
        showExplanation: false,
        backHasFocus: false,
        notesHasFocus: false,
        imgErrors: [],

        loadingImg: false,
        tmpImg: null,
        fileMediaSVG: octicons['file-media'].toSVG(),
        trashcanSVG: octicons['trashcan'].toSVG(),
        deletingImageNote: null,
      }
    },
    computed: {
      hasNote() {
        return this.question.note != null
      },
      cannotSkip() {
        const hasNote = this.question.note !== '' && this.question.note != null
        const noVal = this.question.val == null
        return this.parentLoading || (hasNote && noVal)
      },
      canSubmit() {
        return !this.parentLoading && this.question.val !== undefined
      },
      isVerified() {
        return this.$store.getters['user/isVerified']
      }
    },
    watch: {
      loading(newIsTrue) {
        window.clearTimeout(this.timeoutId)
        this.timeoutId = null

        if (newIsTrue) {
          this.startSpinnerTimeout()
        } else {
          this.showSpinner = false
        }
      },
    },
    created() {
      this.registerKeydownHandler(['ArrowRight', 'Right'], () => {
        const questionVal = this.question.val
        switch (questionVal) {
          case undefined:
            this.selectVal(2)
            break
          case 2:
          case 1:
            this.selectVal(questionVal - 1)
            break
          case 0:
            this.selectVal(null)
        }
      })
      this.registerKeydownHandler(['ArrowLeft', 'Left'], () => {
        const questionVal = this.question.val
        switch (questionVal) {
          case undefined:
            this.selectVal(null)
            break
          case null:
            this.selectVal(0)
            break
          case 0:
          case 1:
            this.selectVal(questionVal + 1)
            break
        }
      })
      this.registerKeydownHandler(['Enter'], () => {
        if (this.backHasFocus) {
          this.back()
        } else if (this.canSubmit && !this.notesHasFocus) {
          this.done()
        } else if (this.notesHasFocus) {
          this.$refs['textarea'].focus()
          return true
        }
      })
    },
    methods: {
      async onInput(obj) {
        this.tmpImg = obj
      },
      async showDeleteImageNote(item) {
        this.deletingImageNote = item
        this.imgErrors = []
        this.$bvModal.show('delete-image-note-modal')
      },
      async deleteImageNoteWrapper(item) {
        this.loadingImg = true
        try {
          await this.deleteImageNote(item)
          this.$bvModal.hide('delete-image-note-modal')
          this.imgErrors = []
        } catch (e) {
          this.catchApiError(e, false, 'imgErrors')
        } finally {
          this.loadingImg = false
        }
      },
      closeImageNoteWrapper() {
        this.tmpImg = null
        this.$bvModal.hide('image-note-modal')
      },
      async saveImageNoteWrapper() {
        this.loadingImg = true
        const [prefix, b64Data] = this.tmpImg.dataUrl.split(';')
        const contentType = prefix.split(':')[1]

        const body = new FormData()
        body.append(
          'image',
          b64toBlob(b64Data.split(',')[1], contentType),
          this.tmpImg.info.name
        )
        try {
          await this.saveImageNote(body)
          this.imgErrors = []
          this.$bvModal.hide('image-note-modal')
        } catch (e) {
          if (e.status === 413) {
            this.imgErrors = ['Max file size 5 MB.']
          } else {
            this.catchApiError(e, false, 'imgErrors')
          }

        } finally {
          this.tmpImg = null
          this.loadingImg = false
        }
      },
      blurBack() {
        setTimeout(() => {
          this.backHasFocus = false
        }, 50)
      },
      notesFocus(status) {
        setTimeout(() => {
          this.notesHasFocus = status
        }, 50)
      },
      startSpinnerTimeout() {
        this.timeoutId = setTimeout(() => {
          this.showSpinner = true
        }, 200)
      },
      clickScreenShot() {
        this.imgErrors = []
        this.$bvModal.show('image-note-modal')
      },
      showNote() {
        if (this.parentLoading) {
          return null
        }
        this.showToggle = true
      },
      selected() {
        return this.question.val !== undefined
      },
      selectVal(val) {
        this.question.val = val
        this.$refs[`value-${val}`].focus()
      },
      back() {
        this.showToggle = false
        this.prev()
        document.activeElement.blur()
      },
      async done() {
        if (this.question.note === "") {
          this.question.note = null
        }
        this.showToggle = false
        await this.next(this.question)
        document.activeElement.blur()
      }
    }
  }
</script>

<style scoped lang="scss">
  @import "../../../assets/scss/variables";

  hr {
    margin: 1em 0 3em;
  }

  @media (min-width: 768px) {
    .text-container {
      padding: 1em 5em;
    }
  }

  .right-note {
    clear: right;
    margin-bottom: 1em;
  }

  .question-text {
    font-size: 1.3em;
    z-index: 2;
    position: relative;
    background-color: white;
    margin: 0;
    padding-bottom: 0.8em;
  }

  .explanation {
    margin-top: 0;
    background-color: $light-grey;
    padding: 1em 1.5em;
    border-radius: .15em;
    overflow: hidden;
    transform-origin: top;
    transition: transform .3s ease-in-out;
    z-index: 1;
  }

  .explanation-enter, .explanation-leave-to {
    transform: scaleY(0.5) translateY(-100%);
  }

  .explanation-toggle {
    margin: 0;
    text-align: right;
    /*border-bottom: 1px solid grey;*/
    position: relative;
    z-index: 2;
    background-color: white;
    @media (max-width: $sm-breakpoint-max) {
      text-align: center;
      padding-top: 1em;
    }
  }

  .options {
    margin-right: 0em;
    margin-left: 0em;
    padding: 0;

    button {
      margin: 1em 0.5em 0;

      &:not(:disabled):not(.disabled):active {
        background-color: $red;
        border-color: $red;
        color: white;
      }

      &:first-child {
        margin-left: 0;
      }

      &:last-child {
        margin-right: 0;
      }

      @media (max-width: $md-breakpoint-max) {
        margin: 0.5em 0;
      }
    }
  }

  button {
    min-width: 6em;
    @media (max-width: $sm-breakpoint-max) {
      margin-top: 1em;
      width: 90%;
    }
  }

  .navigation {
    text-align: center;

    .backward:not(:disabled) {
      &:hover, &:focus {
        ::v-deep .octicon {
          fill: $dark-grey;
        }
      }
    }

    .forward {
      float: right;

      button {
        float: right;
        clear: both;

        &:not(:disabled) {
          &:hover, &:focus {
            ::v-deep .octicon {
              fill: $red;
            }
          }
        }
      }

      .button-hint {
        float: right;
        clear: both;
        font-size: .8em;
        margin-top: .3em;
        margin-right: .5em;
        @media (max-width: $md-breakpoint-max) {
          display: none;
        }

        &.disabled {
          opacity: .65;
        }
      }
    }
  }

  .strong {
    font-weight: bold;
  }

  .image-note-link {
    display: inline-block;
  }

  .backward {
    float: left;
  }

  .forward, .backward {
    width: auto;
  }

  ::v-deep .modal {
    padding-top: 6em;
  }

  ::v-deep .octicon {
    position: relative;
    top: -1px;
  }
</style>